import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Rocket } from "lucide-react";
import api from "@/lib/api";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { PlanChangePreview } from "@/components/merchant/billing/PlanChangePreview";

const CADENCES = [["monthly", "Monthly"], ["annual", "Annual · 2 months free"]];

export function UpgradeToProDialog({ open, onClose, defaultCadence = "monthly", onUpgraded }) {
  const [cadence, setCadence] = useState(defaultCadence);
  const [ready, setReady] = useState(false);
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) setCadence(defaultCadence); }, [open, defaultCadence]);

  const confirm = async () => {
    try {
      setBusy(true);
      await api.post("/billing/sg/change-plan", { plan: "growth", cadence });
      toast.success("You're on Pro now.");
      onClose();
      onUpgraded?.();
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Could not upgrade right now.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && !busy && onClose()}>
      <DialogContent className="max-w-md" data-testid="upgrade-pro-dialog">
        <DialogHeader>
          <DialogTitle className="inline-flex items-center gap-2"><Rocket className="w-5 h-5 text-cyan-600" /> Upgrade to Pro</DialogTitle>
          <DialogDescription className="text-sm text-slate-600 pt-1" data-testid="upgrade-pro-dialog-copy">
            Pro starts right away: 3 storefronts, 4 provinces each, every AI tool, MCP connectors, cohort analysis and a lower fee.
          </DialogDescription>
        </DialogHeader>
        <div className="inline-flex p-1 rounded-full bg-slate-100 text-xs font-semibold w-fit" data-testid="upgrade-pro-cadence">
          {CADENCES.map(([c, label]) => (
            <button key={c} type="button" onClick={() => setCadence(c)} disabled={busy} data-testid={`upgrade-pro-cadence-${c}`}
                    className={`px-3 py-1.5 rounded-full transition-colors ${cadence === c ? "bg-slate-900 text-white" : "text-slate-600 hover:text-slate-900"}`}>
              {label}
            </button>
          ))}
        </div>
        <PlanChangePreview plan="growth" cadence={cadence} onReady={setReady} />
        <DialogFooter className="flex-row justify-end gap-2 sm:gap-2">
          <Button variant="outline" onClick={onClose} disabled={busy} data-testid="upgrade-pro-cancel" className="rounded-full text-xs">Not now</Button>
          <Button onClick={confirm} disabled={busy || !ready} data-testid="upgrade-pro-confirm" className="rounded-full text-xs">
            {busy && <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" />}Confirm upgrade
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
